import { memo, useCallback, useMemo, useRef, useState } from 'react';

import type { ErrorSchema } from '@rjsf/utils';
import { nanoid } from 'nanoid';

import type { TraceContext } from '@/components/newLink/newLink.types';
import { notify } from '@/components/toast';
import { BatchEditModal } from '@/components/ui/dataEditor/batchEditModal/batchEditModal';
import { DEFAULT_SELECTOR } from '@/components/ui/dataEditor/form/tableEditor.form.constant';
import type { TableEditorConfiguration } from '@/components/ui/dataEditor/form/tableEditor.form.type';
import {
  applyModifiedDataChangeInPlace,
  type DataEditorAction
} from '@/components/ui/dataEditor/reducersInPlace';
import Table from '@/components/ui/table';
import { deepCopy } from '@/utils';
import type { TableRow, TableUpdate } from '../../table/table.type';

type BatchCache = {
  cache: TableRow[];
  nbRows: number;
};

type Props = {
  title?: string;
  description?: string;
  required: boolean | undefined;
  value: TableRow[];
  config: TableEditorConfiguration;
  onValueChange: React.Dispatch<React.SetStateAction<TableRow[]>>;
  selectedRows: string[];
  onSelectedRowsChange: (ids: string[]) => void;
  cachedUpdatesKey?: string;
  errorSchema?: ErrorSchema<Record<string, any>>;
  traceContext?: TraceContext;
};

export const FormTableEditor = memo((props: Props) => {
  const { table: tableConfig } = props.config;
  const { selectedRows, onSelectedRowsChange: setSelectedRows } = props;

  const [toastId, setToastId] = useState<Maybe<ReturnType<typeof notify>>>();
  const batchUpdateCacheRef = useRef<Maybe<BatchCache>>(undefined);

  const dataSelectorPath = useMemo<string>(
    () => tableConfig.dataSelectorPath || DEFAULT_SELECTOR,
    [tableConfig.dataSelectorPath]
  );

  const onDataChange = (action: DataEditorAction) => {
    applyModifiedDataChangeInPlace(
      props.onValueChange,
      dataSelectorPath,
      action,
      tableConfig.dataHook
    );
    if (toastId) {
      // close the previous toast if any
      notify.dismiss(toastId);
      setToastId(undefined);
    }
  };

  const [showBatchModal, setShowBatchModal] = useState(false);

  const toggleBatchModal = useCallback(
    () => setShowBatchModal(prev => !prev),
    []
  );

  const tableUpdate: TableUpdate = useMemo(
    () => ({
      userDisplay: {
        localStorageKey: `${props.cachedUpdatesKey}_tableConfig`
      },
      initialSelectedRows: selectedRows,
      setSelectedRows,
      edit: {
        modifiedData: {}, // modifiedData,
        onDataModified: onDataChange,
        onClickBatchEdit: toggleBatchModal,
        batchEditClicked: showBatchModal,
        allowDisplayDiffs: false,
        onDeleteSelectedRows: (rowIds: string[]) => {
          onDataChange({
            type: 'delete_rows',
            rowSelectors: rowIds
          });
        }
      },
      onClickNew:
        props.config.onClickNewEnabled === true
          ? () => {
              onDataChange({
                type: 'add_row',
                newRow: { [dataSelectorPath]: nanoid(8) }
              });
            }
          : undefined,
      newClicked: false,
      onClickArchive: undefined,
      archiveClicked: false
    }),
    [
      props.cachedUpdatesKey,
      selectedRows,
      showBatchModal,
      toggleBatchModal,
      setSelectedRows
    ]
  );

  const undoBatchUpdate = () => {
    const batchUpdateCache = batchUpdateCacheRef.current;

    onDataChange({
      type: 'set',
      modifiedData: batchUpdateCache?.cache
    });
    batchUpdateCacheRef.current = undefined;
  };

  const submitBatchPatch = (patch, editablePaths) => {
    // close the modal
    toggleBatchModal();

    // keep track of modifiedData just before batch edit
    const cache = {
      cache: deepCopy(props.value),
      nbRows: selectedRows.length
    };
    batchUpdateCacheRef.current = cache;

    // update the batch
    onDataChange({
      type: 'batch_update',
      rowSelectors: selectedRows,
      patch,
      editablePaths
    });
    const newToastId = notify(
      `${selectedRows.length} ${selectedRows.length > 1 ? 'rows have' : 'row has'} been edited`,
      {
        position: 'bottom-right',
        duration: 5000,
        cancel: {
          label: 'Undo',
          onClick: undoBatchUpdate
        }
      }
    );
    setToastId(newToastId);
  };

  const headerConfig = {
    isCompact: true,
    newLabel: 'New row'
  } as const;

  return (
    <>
      <div className="relative flex size-full flex-col flex-nowrap items-center">
        <div className="size-full overflow-hidden rounded-md border border-input">
          <Table
            className="border-none"
            config={tableConfig}
            data={props.value}
            errorSchema={props.errorSchema}
            headerConfig={headerConfig}
            update={tableUpdate}
          />
        </div>
      </div>

      <BatchEditModal
        columns={tableConfig.columns}
        onOpenChange={setShowBatchModal}
        onSubmit={submitBatchPatch}
        open={showBatchModal}
        selectedRows={selectedRows}
      />
    </>
  );
});
